import {
  isParticipantWorker,
  Participant,
  ParticipantField,
  ProfileFieldCategory,
  useCurrentParticipant,
  useParticipantFieldsByName,
  useParticipantFieldsByProfileCategory,
} from '@app/api';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';

interface Completion {
  answered: number;
  length: number;
  percentage: number;
}

const isEmptyField = (field: unknown) => {
  if (typeof field === 'number') {
    return false;
  }
  return isEmpty(field);
};

const getCompletionForFields = (
  list: ParticipantField[],
  participant: Participant,
): Completion => {
  const res = list.reduce(
    (prev, p) => prev + (!isEmptyField(participant[p.name]) ? 1 : 0),
    0,
  );
  return {
    answered: res,
    length: list.length,
    percentage: Math.trunc((res / list.length) * 100),
  };
};

export const useProfileCompletion = () => {
  const { data: participant } = useCurrentParticipant();
  const fieldsByCat = useParticipantFieldsByProfileCategory();
  const fields = useParticipantFieldsByName();

  return useMemo(() => {
    const res: { [p: string]: Completion } = {};
    if (!participant) return;
    let totalCompletionVal = 0;
    let totalCompletionCount = 0;
    // eslint-disable-next-line prefer-const
    for (let [profileCategory, participantFields] of Object.entries(
      fieldsByCat,
    )) {
      if (profileCategory === ProfileFieldCategory.PROFESSIONAL) {
        const professionalSituationField = fields['professionalSituation'];
        if (isParticipantWorker(participant)) {
          if (!participantFields.includes(professionalSituationField)) {
            participantFields.push(professionalSituationField);
          }
        } else {
          participantFields = [professionalSituationField];
        }
      }
      res[profileCategory] = getCompletionForFields(
        participantFields,
        participant,
      );
      if (profileCategory === ProfileFieldCategory.OTHERS) {
        continue;
      }
      totalCompletionVal += res[profileCategory].answered;
      totalCompletionCount += res[profileCategory].length;
    }
    res['GLOBAL'] = {
      answered: totalCompletionVal,
      length: totalCompletionCount,
      percentage: Math.trunc((totalCompletionVal / totalCompletionCount) * 100),
    };
    return res;
  }, [participant, fieldsByCat, fields]);
};
